// Firebase Cloud Messaging (FCM) Web Push setup
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import api from './api';
import { devError, devLog } from './devLog';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let messagingInstance = null;

const isConfigured = () =>
  Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.messagingSenderId && VAPID_KEY);

export const getFirebaseApp = () => {
  if (!isConfigured()) return null;
  if (getApps().length) return getApp();
  return initializeApp(firebaseConfig);
};

export const getMessagingInstance = async () => {
  if (messagingInstance) return messagingInstance;
  if (!isConfigured()) return null;
  try {
    const supported = await isSupported();
    if (!supported) {
      devLog('FCM not supported on this browser');
      return null;
    }
    const app = getFirebaseApp();
    if (!app) return null;
    messagingInstance = getMessaging(app);
    return messagingInstance;
  } catch (e) {
    devError('Failed to init messaging:', e);
    return null;
  }
};

/**
 * Request permission and register an FCM token for the current authenticated user.
 * Idempotent — safe to call on every dashboard mount.
 */
export const registerPushNotifications = async ({ silent = false } = {}) => {
  if (!isConfigured()) {
    return { ok: false, reason: 'not_configured' };
  }
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return { ok: false, reason: 'unsupported' };
  }
  
  try {
    // Register the FCM service worker explicitly
    const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    
    let permission = Notification.permission;
    if (permission === 'default') {
      if (silent) return { ok: false, reason: 'permission_default' };
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') {
      return { ok: false, reason: 'denied' };
    }
    
    const messaging = await getMessagingInstance();
    if (!messaging) return { ok: false, reason: 'no_messaging' };
    
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    
    if (!token) {
      return { ok: false, reason: 'no_token' };
    }
    
    // Register token with backend
    await api.post('/notifications/fcm-token', {
      token,
      platform: 'web',
      user_agent: navigator.userAgent || '',
    });
    
    try {
      localStorage.setItem('fcm_token', token);
    } catch (storageErr) {
      // localStorage may be disabled (e.g. Safari private mode); token is still valid on the server.
      devError('Failed to cache FCM token in localStorage:', storageErr);
    }
    
    return { ok: true, token };
  } catch (e) {
    devError('FCM registration failed:', e);
    return { ok: false, reason: 'error', error: e.message };
  }
};

/**
 * Listen for foreground messages (when app is open).
 * Returns an unsubscribe function.
 */
export const onForegroundMessage = (handler) => {
  let unsub = () => {};
  getMessagingInstance().then(messaging => {
    if (!messaging) return;
    unsub = onMessage(messaging, (payload) => {
      try { handler(payload); } catch (e) { devError('onMessage handler error:', e); }
    });
  });
  return () => unsub();
};

/**
 * Unregister current FCM token on logout.
 */
export const unregisterPushNotifications = async () => {
  try {
    const token = localStorage.getItem('fcm_token');
    if (!token) return;
    await api.delete('/notifications/fcm-token', { data: { token } });
    localStorage.removeItem('fcm_token');
  } catch (e) {
    devError('FCM unregister failed:', e);
  }
};

export const isPushSupported = () => isConfigured() && 'Notification' in window && 'serviceWorker' in navigator;
export const getPushPermission = () => (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
